<!--
  A downloaded .ics is not a calendar entry.

  This offered one action: download the file. On Gmail that is the hardest
  possible path — Google Calendar hides import behind Settings → Import & export,
  and on a phone the file lands in Downloads and dies there. So the export was
  real and the appointment still never reached the rep's calendar.

  The file stays (Apple Calendar, Outlook desktop, and one import for a whole
  route), and the one-click destinations sit beside it. Google and Outlook
  compose a single event per link, so a three-stop day is three taps there and
  one file — which is exactly why both are offered rather than one replacing
  the other.
-->
<template>
  <span>
    <v-menu :close-on-content-click="false" v-model="open" location="bottom start">
      <template #activator="{ props: activator }">
        <!-- .stop only: the activator's own handler owns opening, and setting
             `open` here as well toggled it straight back shut. -->
        <v-btn v-bind="activator" variant="text" size="small" prepend-icon="mdi-calendar-plus"
          :loading="busy" @click.stop>{{ label }}</v-btn>
      </template>
      <v-card min-width="300" max-width="420" data-test="calendar-options">
        <v-list density="compact">
          <v-list-item v-if="busy" title="Reading the saved dates…" />
          <template v-else-if="error">
            <v-list-item>
              <p class="text-body-2 text-error">{{ error }}</p>
              <v-btn variant="text" size="small" class="mt-1" @click="load">Try again</v-btn>
            </v-list-item>
          </template>
          <template v-else-if="invite">
            <template v-for="(event, index) in invite.events" :key="event.uid">
              <v-divider v-if="index" class="my-1" />
              <v-list-subheader v-if="invite.events.length > 1" class="text-wrap">{{ event.title }}</v-list-subheader>
              <v-list-item :href="event.google" target="_blank" rel="noopener"
                prepend-icon="mdi-google" title="Google Calendar" @click="open = false" />
              <v-list-item :href="event.outlook" target="_blank" rel="noopener"
                prepend-icon="mdi-microsoft-outlook" title="Outlook" @click="open = false" />
            </template>
            <v-divider class="my-1" />
            <v-list-item prepend-icon="mdi-download" :title="fileTitle"
              subtitle="Apple Calendar, Outlook desktop, anything else" @click="download" />
            <v-list-item prepend-icon="mdi-content-copy" :title="copyLabel" @click="copy" />
            <v-list-item>
              <p class="text-caption text-medium-emphasis text-wrap">
                A snapshot, not a live subscription — later changes here will not reach a calendar
                you have already added this to. Nobody is invited or emailed.
              </p>
            </v-list-item>
          </template>
        </v-list>
      </v-card>
    </v-menu>
    <div v-if="error && !open" class="text-error text-caption" role="alert">{{ error }}</div>
  </span>
</template>
<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import fieldApi from '@/services/fieldSalesApi'
import crmApi from '@/services/crmApi'
const props = defineProps({ kind: String, id: [String, Number], auto: Boolean, label: { type: String, default: 'Add to calendar' } })
const busy = ref(false)
const open = ref(false)
const error = ref('')
const invite = ref(null)
const copyLabel = ref('Copy the details')

const fileTitle = computed(() => (invite.value?.eventCount > 1
  ? `Download all ${invite.value.eventCount} as one file`
  : 'Download the calendar file'))

async function load () {
  busy.value = true; error.value = ''
  try {
    invite.value = await fieldApi.calendar(props.kind, props.id)
  } catch (e) {
    invite.value = null
    const message = crmApi.describeError(e)
    error.value = [message.error, message.remedy].filter(Boolean).join(' ')
  } finally { busy.value = false }
}

// Re-read on each open: a route can be re-sequenced or a delivery re-dated
// between two glances at the same screen.
watch(open, (isOpen) => { if (isOpen) load() })

async function download () {
  if (!invite.value) return
  const url = URL.createObjectURL(new Blob([invite.value.content], { type: invite.value.mimeType }))
  const link = document.createElement('a'); link.href = url; link.download = invite.value.filename
  document.body.appendChild(link); link.click(); link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  open.value = false
}

/** Plain text for a client neither link covers, or for a message to a teammate. */
async function copy () {
  if (!invite.value) return
  const text = invite.value.events.map((event) => [
    event.title,
    [event.date, event.window].filter(Boolean).join(' '),
    event.location,
    event.url
  ].filter(Boolean).join('\n')).join('\n\n')
  try {
    await navigator.clipboard.writeText(text)
    copyLabel.value = 'Copied'
    setTimeout(() => { copyLabel.value = 'Copy the details' }, 2000)
  } catch {
    // Clipboard access is refused outside a secure context and in some browsers.
    // Say which, rather than leaving a button that silently does nothing.
    error.value = 'This browser would not let the page copy. Select the details from the calendar file instead.'
  }
}

// The post-redirect banner asks for the file itself, not a menu.
onMounted(async () => { if (props.auto) { await load(); if (invite.value) download() } })
</script>
